import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Edit, 
  Trash, 
  Users, 
  Building, 
  MapPin, 
  Zap, 
  BarChart3, 
  Download,
  Calendar,
  CreditCard,
  Plus,
  User
} from "lucide-react";
import { Gerador, Cliente } from "@/types";
import { GeradorService, ClienteService } from "@/services/StorageService";
import Layout from "@/components/layout/Layout";
import { toast } from "sonner";
import GeradorEditForm from "@/components/geradores/GeradorEditForm";

const GeradorDetalhes = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [gerador, setGerador] = useState<Gerador | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (id) {
      loadGeradorData();
    }
  }, [id]);

  const loadGeradorData = () => {
    if (id) {
      const geradorData = GeradorService.getById(id);
      const clientesData = ClienteService.getByGeradorId(id);
      
      setGerador(geradorData || null);
      setClientes(clientesData);
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (id) {
      // Primeiro verificar se há clientes vinculados
      if (clientes.length > 0) {
        toast.error("Não é possível excluir um gerador com clientes vinculados. Remova os clientes primeiro.");
        return;
      }
      
      // Executar a exclusão
      const success = GeradorService.delete(id);
      
      if (success) {
        toast.success("Gerador excluído com sucesso!");
        navigate("/geradores");
      } else {
        toast.error("Erro ao excluir o gerador.");
      }
    }
  };

  const handleSaveEdit = (updatedGerador: Gerador) => {
    setGerador(updatedGerador);
    setIsEditing(false);
  };

  const handleExportClientes = () => {
    if (clientes.length === 0) return;
    
    const headers = ['Nome', 'Documento', 'Cidade', 'Estado', 'Concessionária', 
                    'Consumo (kWh)', 'Gasto (R$)', 'Economia (R$)', 'Economia (%)'];
    
    const rows = clientes.map(c => [
      c.nome,
      c.documento,
      c.cidade,
      c.estado,
      c.concessionaria,
      c.consumoMedio?.toString() || '0',
      c.gastoEnergia.toString(),
      c.economiaEstimada?.toString() || '0',
      c.percentualEconomia?.toString() || '0'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `clientes_gerador_${id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p>Carregando...</p>
        </div>
      </Layout>
    );
  }

  if (!gerador) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-lg text-muted-foreground mb-4">Gerador não encontrado</p>
          <Link to="/geradores">
            <Button>Voltar para lista de geradores</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const creditosUtilizados = gerador.geracaoEstimada - gerador.creditosDisponiveis;
  const percentualUtilizado = (creditosUtilizados / gerador.geracaoEstimada) * 100;

  return (
    <Layout>
      <div className="space-y-6">
        {isEditing ? (
          <GeradorEditForm 
            gerador={gerador}
            onSave={handleSaveEdit}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-energy-green-dark">{gerador.nomeTitular}</h1>
                <p className="text-muted-foreground flex items-center mt-1">
                  <MapPin className="h-4 w-4 mr-1" />
                  {gerador.cidade}/{gerador.estado} - {gerador.concessionaria}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  className="border-energy-green text-energy-green hover:bg-energy-green/10"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10">
                      <Trash className="h-4 w-4 mr-2" />
                      Excluir
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este gerador? Esta ação não pode ser desfeita.
                        {clientes.length > 0 && (
                          <p className="font-medium text-destructive mt-2">
                            Este gerador possui {clientes.length} cliente(s) vinculado(s). Remova os clientes primeiro.
                          </p>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDelete}
                        disabled={clientes.length > 0}
                        className={clientes.length > 0 ? "bg-muted" : ""}
                      >
                        Confirmar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building className="h-5 w-5 mr-2" />
                      Dados do Gerador
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Titular</h3>
                        <p>{gerador.nomeTitular}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Documento</h3>
                        <p>{gerador.documento}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Endereço</h3>
                        <p>{gerador.logradouro}, {gerador.bairro}</p>
                        <p>{gerador.cidade}/{gerador.estado} - CEP: {gerador.cep}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Concessionária</h3>
                        <p>{gerador.concessionaria}</p>
                      </div>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Potência Instalada</h3>
                        <p className="text-lg font-medium">{gerador.potenciaInstalada.toFixed(2)} kWp</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Geração Estimada</h3>
                        <p className="text-lg font-medium">{gerador.geracaoEstimada.toFixed(2)} kWh/mês</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Tarifa de Venda</h3>
                        <p className="text-lg font-medium">R$ {gerador.tarifaVenda.toFixed(4)}/kWh</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="flex items-center">
                        <Users className="h-5 w-5 mr-2" />
                        Clientes Vinculados
                      </CardTitle>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-energy-blue border-energy-blue hover:bg-energy-blue/10"
                          onClick={handleExportClientes}
                          disabled={clientes.length === 0}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Exportar
                        </Button>
                        <Link to={`/clientes/novo?gerador=${gerador.id}`}>
                          <Button size="sm" className="bg-energy-blue text-white hover:bg-energy-blue-dark">
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <CardDescription>
                      {clientes.length} cliente(s) consumindo esta energia
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Localização</TableHead>
                            <TableHead className="text-right">Consumo</TableHead>
                            <TableHead className="text-right">Economia</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientes.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center h-24 text-muted-foreground">
                                Nenhum cliente vinculado a este gerador
                              </TableCell>
                            </TableRow>
                          ) : (
                            clientes.map((cliente) => (
                              <TableRow key={cliente.id}>
                                <TableCell className="font-medium">
                                  <Link to={`/clientes/${cliente.id}`} className="hover:underline">
                                    <div className="flex items-center">
                                      <User className="h-4 w-4 mr-2 text-energy-blue" />
                                      {cliente.nome}
                                    </div>
                                  </Link>
                                </TableCell>
                                <TableCell>{cliente.cidade}/{cliente.estado}</TableCell>
                                <TableCell className="text-right">
                                  {cliente.consumoMedio?.toFixed(2) || "0.00"} kWh/mês
                                </TableCell>
                                <TableCell className="text-right text-energy-green">
                                  {cliente.percentualEconomia?.toFixed(1) || "0.0"}%
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Zap className="h-5 w-5 mr-2 text-energy-yellow-light" />
                      Créditos Disponíveis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-2">{gerador.creditosDisponiveis.toFixed(2)} kWh</div>
                    <div className="w-full bg-muted rounded-full h-2.5 mb-4">
                      <div 
                        className={`h-2.5 rounded-full ${percentualUtilizado > 80 ? 'bg-energy-yellow-dark' : 'bg-energy-green'}`} 
                        style={{ width: `${percentualUtilizado}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Utilizados: {creditosUtilizados.toFixed(2)} kWh</span>
                      <span className="text-muted-foreground">{percentualUtilizado.toFixed(1)}%</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2 text-energy-blue" />
                      Análise Financeira
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Custo de Investimento</h3>
                      <p className="text-lg font-medium">R$ {gerador.custoInvestimento.toFixed(2)}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Valor Geração Mensal</h3>
                      <p className="text-lg font-medium">
                        R$ {gerador.valorGeracaoEstimada?.toFixed(2) || "N/A"}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Ganho com Venda</h3>
                      <p className="text-lg font-medium text-energy-blue">
                        R$ {gerador.receitaTotal?.toFixed(2) || "0.00"}/mês
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Payback</h3>
                      <p className="text-lg font-medium">
                        {gerador.payback?.toFixed(2) || "N/A"} anos
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Valor kWh em 25 anos</h3>
                      <p className="text-lg font-medium">
                        R$ {gerador.valorKwhMes?.toFixed(4) || "N/A"}/kWh
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar className="h-5 w-5 mr-2 text-energy-green" />
                      Economia Projetada
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Economia Mensal Total</h3>
                      <p className="text-xl font-medium text-energy-green">
                        R$ {clientes.reduce((acc, c) => acc + (c.economiaEstimada || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Economia Anual Projetada</h3>
                      <p className="text-lg font-medium">
                        R$ {(clientes.reduce((acc, c) => acc + (c.economiaEstimada || 0), 0) * 12).toFixed(2)}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">Média de Economia por Cliente</h3>
                      <p className="text-lg font-medium">
                        {clientes.length > 0 
                          ? `${(clientes.reduce((acc, c) => acc + (c.percentualEconomia || 0), 0) / clientes.length).toFixed(1)}%`
                          : "N/A"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default GeradorDetalhes;
